import { apiClient } from '@/lib/api-client';
import { API_CONFIG } from '@/config/api';

/**
 * Cart item additional data interface
 */
export interface CartItemAdditional {
  product_id: string;
  quantity: number;
  shipping_type: string;
  seller_lat: string;
  seller_lang: string;
  customer_pincode: string;
  seller_pincode: string;
  item_weight: number;
  moq: number;
  gst: number;
  shipCharge: number;
  manufacturer_label: string;
  seller_info?: {
    product_id: string;
    is_owner: number;
    seller_id: number;
    mp_seller_id: string;
    manufacturer_label: string | null;
  };
}

/**
 * Cart item interface
 */
export interface CartItemData {
  id: number;
  product_id: number;
  product_type: string;
  name: string;
  sku: string;
  quantity: string;
  price: string;
  total: string;
  tax_total: string;
  base_total: string;
  additional: CartItemAdditional;
  product: {
    id: number;
    name: string;
    url_key: string;
    images: Array<{
      id: number;
      type: string | null;
      path: string;
      product_id: number;
      position: number;
      url: string;
    }>;
    price: string;
    base_price: string;
    total: string;
    tax_total: string;
  };
}

/**
 * Cart data interface
 */
export interface CartData {
  id: number;
  items_count: number;
  items_qty: string;
  items: CartItemData[];
  base_grand_total: string;
  grand_total: string;
  base_sub_total: string;
  sub_total: string;
  tax_total: string;
  discount_amount: string;
  coupon_code: string | null;
  applied_cart_rule_ids: string;
  formatted: {
    grand_total: string;
    sub_total: string;
    tax_total: string;
    discount_amount: string;
  };
}

/**
 * Add to cart request interface
 */
export interface AddToCartRequest {
  product_id: number;
  quantity: number;
  shipping_type: number;
  seller_lat: string;
  seller_lang: string;
  customer_pincode: string;
  seller_pincode: string;
  item_weight: string;
  moq: string;
  gst: string;
  shipCharge: number;
  manufacturer_label: string;
}

/**
 * Add to cart response interface
 */
export interface AddToCartResponse {
  status: boolean;
  message: string;
  data: {
    product_id: string;
    product_name: string;
    quantity_added: number;
    is_buy_now: boolean;
    marketplace_data: {
      shipping_type: string;
      seller_coordinates: {
        lat: string;
        lang: string;
      };
      customer_coordinates: {
        lat: string | null;
        lang: string | null;
      };
      customer_pincode: string;
      seller_pincode: string;
      item_weight: number;
      moq: number;
      gst: number;
      shipCharge: number;
      manufacturer_label: string;
    };
    cart: CartData;
  };
}

/**
 * Cart service for handling cart-related API calls
 */
export class CartService {
  /**
   * Add product to cart
   * @param productId - Product ID to add
   * @param cartData - Cart data including quantity and shipping info
   * @returns Promise with cart response
   */
  static async addToCart(productId: number, cartData: AddToCartRequest): Promise<AddToCartResponse> {
    console.log('Calling add to cart API:', `${API_CONFIG.ENDPOINTS.CART}/add/${productId}`, cartData);
    
    const response = await apiClient.post<AddToCartResponse>(
      `${API_CONFIG.ENDPOINTS.CART}/add/${productId}`,
      cartData
    );

    console.log('Add to cart API response:', response);
    return response;
  }

  /**
   * Get cart details
   * @returns Promise with cart data
   */
  static async getCart(): Promise<CartData> {
    const response = await apiClient.get<{ status: boolean; message: string; data: CartData }>(
      `${API_CONFIG.ENDPOINTS.CART}`
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to fetch cart');
    }

    return response.data;
  }

  /**
   * Update cart item quantity
   * @param itemId - Cart item ID
   * @param quantity - New quantity
   * @returns Promise with updated cart
   */
  static async updateCartItem(itemId: number, quantity: number): Promise<CartData> {
    const response = await apiClient.put<{ status: boolean; message: string; data: CartData }>(
      `${API_CONFIG.ENDPOINTS.CART}/item/${itemId}`,
      { 
        cart_item_id: itemId,
        quantity 
      }
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to update cart item');
    }

    return response.data;
  }

  /**
   * Remove item from cart
   * @param itemId - Cart item ID
   * @returns Promise with updated cart
   */
  static async removeFromCart(itemId: number): Promise<CartData> {
    const response = await apiClient.delete<{ status: boolean; message: string; data: CartData }>(
      `${API_CONFIG.ENDPOINTS.CART}/item/${itemId}`
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to remove cart item');
    }

    return response.data;
  }

  /**
   * Clear entire cart
   * @returns Promise with empty cart
   */
  static async clearCart(): Promise<CartData> {
    const response = await apiClient.delete<{ status: boolean; message: string; data: CartData }>(
      `${API_CONFIG.ENDPOINTS.CART}/clear`
    );

    if (!response.status) {
      throw new Error(response.message || 'Failed to clear cart');
    }

    return response.data;
  }
}